import { useEffect, useState } from "react";
import { Bell, Mail, Check, Loader2, Trophy, Sparkles, Radio, ShoppingBag, LayoutGrid } from "lucide-react";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";

const FIXED = [
  { key: "tournaments", label: "Tournaments", desc: "New events & results", icon: Trophy },
  { key: "promos", label: "News/Promos", desc: "News, new promos and draws", icon: Sparkles },
  { key: "videos", label: "Live streams", desc: "When we go live on YouTube", icon: Radio },
  { key: "merch", label: "Merchandise", desc: "New merch drops", icon: ShoppingBag },
];

export default function Notify() {
  const [divisions, setDivisions] = useState([]);
  const [email, setEmail] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [all, setAll] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/divisions").then((r) => setDivisions((r.data || []).filter((d) => !d.archived))).catch(() => {});
  }, []);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    const categories = all ? ["all"] : Array.from(selected);
    if (categories.length === 0) {
      toast.error("Pick at least one type of update (or choose 'All updates').");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/subscribe", { email, categories });
      if (r.data.status === "updated") {
        toast.success("Your preferences have been updated!");
      } else {
        toast.success("Almost done — check your email to confirm!");
      }
      setEmail("");
      setSelected(new Set());
      setAll(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const cardCls = (active) =>
    `flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
      active ? "border-[#1E63C4] bg-blue-50" : "border-[#E5E7EB] bg-white hover:border-[#1E63C4]/50"
    } ${all ? "opacity-50 pointer-events-none" : ""}`;

  return (
    <Layout>
      <Seo title="Get Notified | Quinte BCA Pool League" path="/notify" noindex />
      <PageHeader eyebrow="Stay in the loop" title="Get Notified" subtitle="Choose what you care about and we'll email you when there's news. Confirm once, unsubscribe anytime." />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <form onSubmit={submit} className="space-y-7" data-testid="notify-form">
          <div className="bg-white rounded-lg border border-[#E5E7EB] shadow-sm p-6">
            <label className="block text-sm font-semibold text-[#111111] mb-1.5">Your email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-5 w-5 text-[#999999]" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="notify-email-input"
                placeholder="you@example.com"
                className="w-full rounded-md border border-[#D1D5DB] pl-11 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#1E63C4]/40 focus:border-[#1E63C4]"
                required
              />
            </div>
          </div>

          {/* All updates */}
          <label
            data-testid="notify-cat-all"
            className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${all ? "border-[#1E63C4] bg-blue-50" : "border-[#E5E7EB] bg-white hover:border-[#1E63C4]/50"}`}
          >
            <input type="checkbox" checked={all} onChange={() => setAll(!all)} className="h-5 w-5 accent-[#1E63C4]" />
            <Bell className="h-5 w-5 text-[#1E63C4]" />
            <div>
              <p className="font-semibold text-[#111111]">All updates</p>
              <p className="text-sm text-[#555555]">Everything below — the simplest option</p>
            </div>
          </label>

          <div>
            <p className="text-sm font-semibold text-[#555555] uppercase tracking-wide mb-3">Or pick specific updates</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {FIXED.map((c) => {
                const active = selected.has(c.key);
                return (
                  <label key={c.key} data-testid={`notify-cat-${c.key}`} className={cardCls(active)}>
                    <input type="checkbox" checked={active} onChange={() => toggle(c.key)} className="mt-0.5 h-5 w-5 accent-[#1E63C4]" />
                    <c.icon className="h-5 w-5 text-[#1E63C4] mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-[#111111]">{c.label}</p>
                      <p className="text-sm text-[#555555]">{c.desc}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {divisions.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-[#555555] uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <LayoutGrid className="h-4 w-4" /> Specific divisions
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {divisions.map((d) => {
                  const key = `division:${d.slug}`;
                  const active = selected.has(key);
                  return (
                    <label key={d.slug} data-testid={`notify-cat-division-${d.slug}`} className={cardCls(active)}>
                      <input type="checkbox" checked={active} onChange={() => toggle(key)} className="mt-0.5 h-5 w-5 accent-[#1E63C4]" />
                      <div>
                        <p className="font-semibold text-[#111111]">{d.name}</p>
                        <p className="text-sm text-[#555555]">{d.night || "Division updates"}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="notify-submit-button"
            className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-7 py-3 font-semibold hover:bg-[#174e9c] disabled:opacity-60 transition-colors"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
            {busy ? "Submitting…" : "Notify me"}
          </button>
          <p className="text-xs text-[#888888]">We&apos;ll send a one-time confirmation email. You can unsubscribe from any message. We never share your email.</p>
        </form>
      </div>
    </Layout>
  );
}
